import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Dropdown, Button } from 'react-bootstrap';
import { GetRequest, PostRequest, PutRequest } from '../Utils/hooks/ApiHandler';
import useLocalStorageState from 'use-local-storage-state';
import { useNavigate } from 'react-router-dom';
import { baseURL } from '../Utils/constants/constant';

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'unread', label: 'Unread' },
  { key: 'read', label: 'Read' },
];
const LIMIT_ALL = 5;
const LIMIT_OTHERS = 10;
const getLimit = (tab) => tab === 'all' ? LIMIT_ALL : LIMIT_OTHERS;

// ─── Helpers ──────────────────────────────────────────────────────────────────
const parseTimezone = (raw) => { try { return JSON.parse(raw)?.label || ''; } catch { return ''; } };
const parseInvitees = (raw) => { try { return JSON.parse(raw) || []; } catch { return []; } };
const relativeTime = (str) => {
  if (!str) return '';
  const diff = Date.now() - new Date(str);
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

// ─── Detail Pane ──────────────────────────────────────────────────────────────
const NotificationDetail = ({ n }) => {
  const invitees = parseInvitees(n.invitees);
  const timezone = parseTimezone(n.timezone);
  return (
    <div className="nd-detail">
      {n.meeting_title && (
        <div className="nd-detail-row">
          <span className="nd-detail-label"><i className="bx bx-calendar-event"></i> Meeting</span>
          <span className="nd-detail-value">{n.meeting_title}</span>
        </div>
      )}
      {n.module_name && (
        <div className="nd-detail-row">
          <span className="nd-detail-label"><i className="bx bx-layer"></i> Module</span>
          <span className="nd-detail-value nd-badge">{n.module_name}</span>
        </div>
      )}
      {(n.meeting_date || n.notification_Date) && (
        <div className="nd-detail-row">
          <span className="nd-detail-label"><i className="bx bx-calendar"></i> Date</span>
          <span className="nd-detail-value">{n.meeting_date || n.notification_Date}</span>
        </div>
      )}
      {n.meeting_from && n.meeting_to && (
        <div className="nd-detail-row">
          <span className="nd-detail-label"><i className="bx bx-time"></i> Time</span>
          <span className="nd-detail-value">{n.meeting_from} – {n.meeting_to}</span>
        </div>
      )}
      {n.meeting_type && (
        <div className="nd-detail-row">
          <span className="nd-detail-label"><i className="bx bx-video"></i> Type</span>
          <span className="nd-detail-value nd-badge nd-badge-soft">{n.meeting_type}</span>
        </div>
      )}
      {n.address && (
        <div className="nd-detail-row">
          <span className="nd-detail-label"><i className="bx bx-map-pin"></i> Address</span>
          <span className="nd-detail-value">{n.address}</span>
        </div>
      )}
      {n.meeting_link && (
        <div className="nd-detail-row">
          <span className="nd-detail-label"><i className="bx bx-link"></i> Link</span>
          <a href={n.meeting_link} target="_blank" rel="noreferrer" className="nd-detail-link">{n.meeting_link}</a>
        </div>
      )}
      {n.meeting_status && (
        <div className="nd-detail-row">
          <span className="nd-detail-label"><i className="bx bx-info-circle"></i> Status</span>
          <span className={`nd-detail-value nd-status nd-status-${(n.meeting_status || '').toLowerCase()}`}>{n.meeting_status}</span>
        </div>
      )}
      {n.description && (
        <div className="nd-detail-row nd-detail-col">
          <span className="nd-detail-label"><i className="bx bx-note"></i> Description</span>
          <span className="nd-detail-value nd-desc">{n.description}</span>
        </div>
      )}
      {timezone && (
        <div className="nd-detail-row">
          <span className="nd-detail-label"><i className="bx bx-globe"></i> Timezone</span>
          <span className="nd-detail-value nd-muted">{timezone}</span>
        </div>
      )}
      {invitees.length > 0 && (
        <div className="nd-detail-row nd-detail-col">
          <span className="nd-detail-label"><i className="bx bx-group"></i> Invitees</span>
          <div className="nd-invitees">
            {invitees.map((inv, idx) => (
              <span key={idx} className="nd-invitee-chip">{inv.fullName || inv.email}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Single Notification Row ──────────────────────────────────────────────────
const NotificationItem = ({ notification, onMarkRead }) => {
  const [open, setOpen] = useState(false);
  const isUnread = notification.is_read === "0" || notification.is_read === null || notification.is_read === undefined;

  const handleToggle = (e) => {
    e.stopPropagation();
    setOpen((v) => !v);
    if (isUnread && !open) {
      onMarkRead(notification.id, notification?.notification_module);
    }
  };

  return (
    <li className={`nd-item${isUnread ? ' nd-item-unread' : ''}${open ? ' nd-item-open' : ''}`}>
      <div className="nd-item-header" onClick={handleToggle}>
        <span className={`nd-avatar ${isUnread ? 'nd-avatar-primary' : 'nd-avatar-muted'}`}>
          <i className={`bx ${isUnread ? 'bxs-bell-ring' : 'bx-bell'}`} style={{ fontSize: 15 }}></i>
        </span>
        <div className="nd-item-body">
          <p className="nd-item-title">
            <span className="nd-item-module">{notification.meeting_title || notification.name || 'Notification'}</span>
            {notification.description && <span className="nd-item-desc"> — {notification.description}</span>}
          </p>
          <span className="nd-item-time">
            <i className="bx bx-time-five" style={{ marginRight: 3 }}></i>
            {relativeTime(notification.created_at)}
          </span>
        </div>
        <span className={`nd-chevron${open ? ' nd-chevron-open' : ''}`}>
          <i className="bx bx-chevron-down"></i>
        </span>
      </div>
      {open && (
        <div className="nd-item-detail">
          <NotificationDetail n={notification} />
        </div>
      )}
    </li>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const NotificationDropdown = () => {
  const navigate = useNavigate();
  const [customer, setCustomer] = useLocalStorageState("customer");
  const customer_id = customer?.customer_details?.id;
  const token = customer?.token?.authorization || "";
  const rf = customer?.token?.authentication || "";
  const extras = { customer, setCustomer, rf, navigate };
  const headers = { Authorization: token, Authentication: rf };

  const [activeTab, setActiveTab] = useState('all');
  const [notifications, setNotifications] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // ── All live values in refs so scroll handler is never stale ─────────────────
  const fetchingRef = useRef(false);
  const activeTabRef = useRef('all');
  const offsetRef = useRef(0);       // bumped ONLY after successful fetch with data
  const hasMoreRef = useRef(true);
  const loadingRef = useRef(false);

  // ── THE KEY FIX: store the scroll handler in a ref so we can attach/detach it
  //    when the DOM node actually appears (Bootstrap hides the menu on mount).
  const scrollHandlerRef = useRef(null);
  // Store the actual <ul> DOM node here via ref-callback (not useRef + useEffect)
  const listNodeRef = useRef(null);

  // ── URL builder ──────────────────────────────────────────────────────────────
  const getUrl = useCallback((tab, off) => {
    if (tab === 'unread') return `${baseURL}Notification/fetchUnreadNotification/${customer_id}?limit=${getLimit(tab)}&offset=${off}`;
    if (tab === 'read') return `${baseURL}Notification/fetchReadNotification/${customer_id}?limit=${getLimit(tab)}&offset=${off}`;
    return `${baseURL}Notification/getNotificationTillCurrentTime/${customer_id}?limit=${getLimit(tab)}&offset=${off}`;
  }, [customer_id]);

  // ── Core fetch — returns true if new data received ───────────────────────────
  const fetchNotifications = useCallback(async (tab, currentOffset, isAppend = false) => {
    if (!customer_id) return false;
    if (fetchingRef.current) return false;

    fetchingRef.current = true;
    loadingRef.current = true;
    setLoading(true);


    let gotData = false;
    try {
      const url = getUrl(tab, currentOffset);
      const res = await GetRequest(url, { headers }, extras);

      if (res && res.status === "success") {
        const newData = Array.isArray(res.data) ? res.data : [];

        if (res.unread !== undefined && res.unread !== null) {
          setUnreadCount(Number(res.unread));
        }

        setNotifications((prev) => {
          if (isAppend) {
            const combined = [...prev, ...newData];
            return Array.from(new Map(combined.map(i => [i.id, i])).values());
          }
          return newData;
        });

        const noMore =
          res.next_offset === "-1" || res.next_offset === -1 || newData.length === 0;

        setHasMore(!noMore);
        hasMoreRef.current = !noMore;
        gotData = newData.length > 0;
      }
    } catch (err) {
      console.error("[ND] fetch error:", err);
    } finally {
      setLoading(false);
      loadingRef.current = false;
      fetchingRef.current = false;
    }

    return gotData;
  }, [customer_id, getUrl]);

  // ── Build scroll handler and keep it updated in a ref ───────────────────────
  // We rebuild this whenever fetchNotifications changes (customer_id change etc.)
  useEffect(() => {
    scrollHandlerRef.current = async () => {
      const el = listNodeRef.current;
      if (!el) return;
      if (loadingRef.current || !hasMoreRef.current || fetchingRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } = el;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

      console.log(`[ND] scroll dist=${distanceFromBottom.toFixed(0)} offset=${offsetRef.current}`);

      if (distanceFromBottom > 40) return;

      const nextOffset = offsetRef.current + 1;
      console.log(`[ND] → fetching offset ${nextOffset}`);

      const gotData = await fetchNotifications(activeTabRef.current, nextOffset, true);

      if (gotData) {
        offsetRef.current = nextOffset;
        console.log(`[ND] offset committed → ${nextOffset}`);
      } else {
        console.log(`[ND] no data — offset stays ${offsetRef.current}`);
      }
    };
  }, [fetchNotifications]);

  // ── ref-callback: called by React whenever the <ul> mounts or unmounts ───────
  // Bootstrap shows/hides the Dropdown.Menu by mounting/unmounting it,
  // so this fires at the RIGHT moment — not on component mount when el is null.
  const listRefCallback = useCallback((node) => {
    // Detach from old node
    if (listNodeRef.current) {
      listNodeRef.current.removeEventListener('scroll', scrollHandlerRef.current);
      console.log('[ND] scroll listener removed');
    }

    listNodeRef.current = node;

    // Attach to new node
    if (node) {
      // Small delay to let Bootstrap finish rendering the dropdown
      setTimeout(() => {
        node.addEventListener('scroll', scrollHandlerRef.current, { passive: true });
        console.log('[ND] scroll listener attached ✓');
      }, 50);
    }
  }, []); // stable — never recreated

  // ── Tab change → full reset + fresh fetch ────────────────────────────────────
  useEffect(() => {
    if (!customer_id) return;
    activeTabRef.current = activeTab;
    offsetRef.current = 0;
    hasMoreRef.current = true;
    fetchingRef.current = false;
    setNotifications([]);
    setHasMore(true);
    fetchNotifications(activeTab, 0, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, customer_id]);

  // ── 5-min background polling ─────────────────────────────────────────────────
  useEffect(() => {
    if (!customer_id) return;
    const id = setInterval(() => {
      if (fetchingRef.current) return;
      offsetRef.current = 0;
      fetchNotifications(activeTabRef.current, 0, false);
    }, 300_000);
    return () => clearInterval(id);
  }, [customer_id, fetchNotifications]);

  // ── Mark read (optimistic) ───────────────────────────────────────────────────
  const handleMarkRead = async (id, notification_module) => {
    // Optimistic UI update immediately
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, is_read: "1" } : n))
    );
    // API call
    try {
      await PutRequest(
        `${baseURL}Notification/markRead`,
        { customer_id, notification_module: notification_module, id },
        { headers },
        extras
      );

    } catch (err) {
      console.error("[ND] markRead error:", err);
      // Revert optimistic update on failure
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: "0" } : n))
      );
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <>
      <Dropdown.Menu
        className="main-header-dropdown header-profile-dropdown dropdown-menu-end shadow border-0 nd-dropdown-menu"
        style={{ width: '380px', padding: 0 }}
      >
        {/* Header */}
        <div className="nd-header">
          <div className="nd-header-top">
            <span className="nd-header-title">
              <i className="bx bxs-bell" style={{ marginRight: 7 }}></i>Notifications
            </span>
            {unreadCount > 0 && (
              <span className="nd-unread-badge">{unreadCount} new</span>
            )}
          </div>

          {/* Tabs */}
          <div className="nd-tabs">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                className={`nd-tab${activeTab === tab.key ? ' nd-tab-active' : ''}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
                {tab.key === 'unread' && unreadCount > 0 && (
                  <span className="nd-tab-count">{unreadCount}</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* ★ ref-callback on the <ul> — fires when element actually enters DOM */}
        <ul
          ref={listRefCallback}
          className="nd-list"
          style={{ maxHeight: 320, overflowY: 'auto' }}
        >
          {notifications.length > 0
            ? notifications.map((n, i) => (
              <NotificationItem
                key={n.id || i}
                notification={n}
                onMarkRead={handleMarkRead}
              />
            ))
            : !loading && (
              <div className="nd-empty">
                <i className="bx bx-bell-off nd-empty-icon"></i>
                <p>No {activeTab !== 'all' ? activeTab : ''} notifications</p>
              </div>
            )}

          {loading && (
            <li className="nd-loader">
              <span className="spinner-border spinner-border-sm" role="status" style={{ marginRight: 6 }}></span>
              Loading…
            </li>
          )}
        </ul>

        {/* Footer */}
        <div className="nd-footer">
          {/* <Button variant="outline-primary" size="sm" className="nd-footer-btn">
            <i className="bx bx-plus" style={{ marginRight: 4 }}></i>New Ticket
          </Button>
          <Button variant="primary" size="sm" className="nd-footer-btn" href="##">
            View All <i className="bx bx-right-arrow-alt" style={{ marginLeft: 4 }}></i>
          </Button> */}
        </div>
      </Dropdown.Menu>

      <style>{`
        .nd-dropdown-menu { overflow: hidden; font-family: 'Inter', system-ui, sans-serif; }

        .nd-header { background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%); padding: 14px 16px 0; }
        .nd-header-top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
        .nd-header-title { color: #fff; font-size: 15px; font-weight: 600; }
        .nd-unread-badge {
          background: rgba(255,255,255,0.2); color: #fff;
          font-size: 11px; font-weight: 600; padding: 2px 9px;
          border-radius: 20px; border: 1px solid rgba(255,255,255,0.35);
        }

        .nd-tabs { display: flex; gap: 4px; }
        .nd-tab {
          flex: 1; background: transparent; border: none;
          color: rgba(255,255,255,0.6); font-size: 13px; font-weight: 500;
          padding: 8px 0; cursor: pointer; position: relative;
          border-bottom: 2px solid transparent;
          transition: color 0.15s, border-color 0.15s;
          display: flex; align-items: center; justify-content: center; gap: 5px;
        }
        .nd-tab:hover { color: #fff; }
        .nd-tab-active { color: #fff !important; border-bottom-color: #fff !important; font-weight: 700; }
        .nd-tab-count {
          background: rgba(255,255,255,0.25); color: #fff;
          font-size: 10px; font-weight: 700; padding: 1px 6px;
          border-radius: 10px; border: 1px solid rgba(255,255,255,0.4); line-height: 1.4;
        }

        .nd-list { list-style: none; margin: 0; padding: 4px 0; background: #fff; }
        .nd-list::-webkit-scrollbar { width: 4px; }
        .nd-list::-webkit-scrollbar-track { background: transparent; }
        .nd-list::-webkit-scrollbar-thumb { background: #ddd; border-radius: 4px; }

        .nd-item { border-bottom: 1px solid #f1f3f7; }
        .nd-item:last-child { border-bottom: none; }
        .nd-item-unread { background: #f5f3ff; }
        .nd-item-open   { background: #fafafa; }

        .nd-item-header {
          display: flex; align-items: flex-start; gap: 10px;
          padding: 11px 14px; cursor: pointer; transition: background 0.12s;
        }
        .nd-item-header:hover { background: #ede9fe44; }

        .nd-avatar { width: 34px; height: 34px; border-radius: 50%; flex-shrink: 0; display: flex; align-items: center; justify-content: center; margin-top: 1px; }
        .nd-avatar-primary { background: #ede9fe; color: #6d28d9; }
        .nd-avatar-muted   { background: #f3f4f6; color: #9ca3af; }

        .nd-item-body { flex: 1; min-width: 0; }
        .nd-item-title { margin: 0 0 3px; font-size: 12.5px; line-height: 1.45; color: #1e1b4b; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
        .nd-item-module { font-weight: 600; color: #4f46e5; }
        .nd-item-desc   { color: #64748b; }
        .nd-item-time   { font-size: 11px; color: #94a3b8; }

        .nd-chevron { color: #94a3b8; font-size: 18px; flex-shrink: 0; margin-top: 2px; transition: transform 0.2s, color 0.2s; }
        .nd-chevron-open { transform: rotate(180deg); color: #6d28d9; }

        .nd-item-detail { background: #faf9ff; border-top: 1px solid #ede9fe; padding: 10px 14px 12px 58px; animation: nd-slide-down 0.18s ease; }
        @keyframes nd-slide-down { from { opacity: 0; transform: translateY(-5px); } to { opacity: 1; transform: translateY(0); } }

        .nd-detail { display: flex; flex-direction: column; gap: 6px; }
        .nd-detail-row { display: flex; align-items: baseline; gap: 8px; font-size: 12px; }
        .nd-detail-col { flex-direction: column; gap: 3px; }
        .nd-detail-label { min-width: 84px; color: #94a3b8; font-size: 10.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; display: flex; align-items: center; gap: 4px; flex-shrink: 0; }
        .nd-detail-value { color: #1e1b4b; font-size: 12.5px; }
        .nd-detail-link  { color: #4f46e5; font-size: 12px; word-break: break-all; }
        .nd-desc  { color: #475569; line-height: 1.5; }
        .nd-muted { color: #64748b; font-size: 11.5px; }

        .nd-badge { background: #ede9fe; color: #6d28d9; padding: 1px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; text-transform: capitalize; }
        .nd-badge-soft { background: #e0f2fe; color: #0369a1; }

        .nd-status { border-radius: 10px; padding: 1px 8px; font-size: 11px; font-weight: 600; }
        .nd-status-scheduled { background: #dcfce7; color: #15803d; }
        .nd-status-completed { background: #dbeafe; color: #1d4ed8; }
        .nd-status-cancelled { background: #fee2e2; color: #dc2626; }

        .nd-invitees { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 2px; }
        .nd-invitee-chip { background: #f3f4f6; border: 1px solid #e5e7eb; border-radius: 10px; padding: 2px 9px; font-size: 11px; color: #374151; }

        .nd-empty { text-align: center; padding: 32px 16px; color: #9ca3af; }
        .nd-empty-icon { font-size: 36px; display: block; margin-bottom: 8px; color: #c4b5fd; }
        .nd-empty p { margin: 0; font-size: 13px; }

        .nd-loader { list-style: none; text-align: center; padding: 10px; font-size: 12px; color: #6d28d9; background: #f5f3ff; }

        .nd-footer { display: flex; gap: 8px; padding: 10px 14px; background: #fafafa; border-top: 1px solid #f1f3f7; }
        .nd-footer-btn { flex: 1; font-size: 12.5px; font-weight: 500; border-radius: 8px; }
      `}</style>
    </>
  );
};

export default NotificationDropdown;